/**
 * Client-side error reporting — the call side.
 *
 * Hands a report to `window.__psErr` and returns. It does not know Sentry
 * exists and it never installs a sink: the sink is a small queueing stub
 * planted by ps-loader on pages in the `sentryPercent` cohort, replaced by
 * event-listener-client's `e.js` with the live reporter once that artifact
 * initialises.
 *
 * This deliberately duplicates `event-listener-client/src/error-report.ts`
 * rather than importing it. The two ship as independent bundles with no shared
 * package between them, and the whole contract is one optional window function
 * — a few bytes of duplication is cheaper than coupling the release trains.
 * Keep the shape identical: `kind` becomes the Sentry issue title and issues
 * group on it, so a drift in wording here splits one issue into two.
 *
 * Nothing here reports on its own. There is no `window.onerror` hook and no
 * instrumentation of any browser API — a report exists only where this package
 * explicitly calls `reportError`.
 */

/**
 * One reportable event.
 *
 * Keep `kind` low-cardinality. Anything that varies per page view belongs in
 * `extra`, never in `kind`, or every occurrence becomes its own issue.
 */
export interface ErrorReport {
  /** Short, stable, human-readable label. Groups the issue. */
  kind: string;
  /** Low-cardinality dimensions, searchable and aggregatable in Sentry. */
  tags?: Record<string, string>;
  /** Free-form diagnostics. Never anything visitor-identifying. */
  extra?: Record<string, string | number | boolean | null>;
}

/**
 * Describe a thrown value without trusting it.
 *
 * A caught value is not necessarily an `Error`, and even when it is, `name` and
 * `message` can be getters that raise. Reading them unguarded at the call site
 * would let the reporting path throw out of the very `catch` meant to contain
 * the failure, which is the one thing this module must never do.
 */
export function describeThrown(thrown: unknown): { error_name: string; error_message: string } {
  try {
    if (thrown instanceof Error) {
      return { error_name: String(thrown.name), error_message: String(thrown.message) };
    }
    return { error_name: typeof thrown, error_message: String(thrown) };
  } catch {
    return { error_name: 'unknown', error_message: 'unstringifiable' };
  }
}

/**
 * Hand a report to the sink, if one is installed.
 *
 * Silent and total by design. No sink is the normal case rather than a fault:
 * session-manager loads on plenty of pages that never receive ps-loader, and on
 * those the report is simply dropped. Reporting a failure must never itself
 * become one.
 */
export function reportError(report: ErrorReport): void {
  try {
    const sink = window.__psErr;
    if (typeof sink === 'function') sink(report);
  } catch {
    // Reporting must never cost the page anything.
  }
}
